/**
 * Copying and pasting nodes in the nodal view, the class diagram and the blueprint (the board
 * has its own, `state/board`; both write what `state/clipboard.ts` reads, so nodes go from one
 * view to another). Keys come through the page's clipboard events (`paste.ts`), the right-click
 * menu through the system's clipboard (`navigator.clipboard`).
 */
import { unwrap } from "solid-js/store";
import type { Graph } from "@protocol/Graph";
import type { Actions } from "../../client/actions";
import type { GraphMirror } from "../../client/graph";
import type { Board } from "../../state/board";
import { copyNodes, groupEdits, pasteOf, readCopied } from "../../state/clipboard";
import type { Diagram } from "../../state/diagram";
import type { KindsService } from "../../state/kinds";
import type { Scene } from "../../state/scene";
import { drawsBoxes, type View } from "../../state/view";
import { toast } from "../../shared/toast";
import { viewPointAt } from "./spot";

/** What copying and pasting needs. */
export interface NodeClipboardDeps {
  graph: GraphMirror;
  view: View;
  scene: Scene;
  diagram: Diagram;
  board: Board;
  actions: Actions;
  kinds: Pick<KindsService, "toDefine">;
}

/** A page point (like a mouse event's `clientX`/`clientY`). */
type PagePoint = { x: number; y: number };

/** What the right-click menu offers (`menu.ts`). */
export interface ClipboardEntries {
  /** Copies the nodes `ids` to the system's clipboard. */
  copy(ids: readonly string[]): void;
  /** Pastes the system's clipboard where the menu was opened. */
  pasteHere(): void;
}

/** Copy and paste of nodes. */
export interface NodeClipboard {
  /** The nodes `ids` as clipboard text, with where they sit in the nodal view and the diagram (`null`: none). */
  copy(ids: readonly string[]): string | null;
  /** Pastes the nodes `text` holds at page point `at` (`null`: the middle of the view), linked as they were, selected; one undo. */
  paste(text: string, at: PagePoint | null): Promise<void>;
  /** The menu's entries for a right click at page point `at`. */
  menu(at: PagePoint): ClipboardEntries;
}

/** Copy and paste of nodes for the graph window `stage()`. */
export function nodeClipboard({ graph, view, scene, diagram, board, actions, kinds }: NodeClipboardDeps, stage: () => HTMLElement): NodeClipboard {
  const clipboard: NodeClipboard = {
    copy(ids) {
      if (!ids.length) return null;
      const boxes = diagram.layout().positions;
      return copyNodes(unwrap(graph.state.graph) as Graph, ids, {
        nodal: (id) => scene.byId.get(id),
        diagram: (id) => boxes[id],
        board: () => undefined,
      });
    },
    async paste(text, at) {
      const copied = readCopied(text);
      if (!copied) return void toast("The clipboard holds no nodes to paste here.");
      const host = stage();
      const r = host.getBoundingClientRect();
      const page = at ?? { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      const boxes = drawsBoxes(view.mode());
      const pasted = pasteOf(unwrap(graph.state.graph) as Graph, copied, boxes ? "diagram" : "nodal", viewPointAt({ view, scene, diagram, board }, host, page.x, page.y));
      for (const n of pasted.nodes) for (const c of n.categories.length ? n.categories : [""]) view.showCategory(c);
      for (const l of pasted.links) view.showLinkKind(l.kind);
      for (const h of pasted.hints) scene.hint(h.name, h.x, h.y);
      const define = kinds.toDefine([...new Set(pasted.nodes.map((n) => n.kind))], [...new Set(pasted.links.map((l) => l.kind))]);
      // A board's frame pasted here becomes a group holding what it held.
      const ids = await actions.addNodes(pasted.nodes, pasted.links, [...define, ...(boxes ? diagram.fixEdits() : []), ...groupEdits(pasted.groups)]);
      if (!ids?.length) return;
      view.selectNodes(ids);
      const what = ids.length === 1 ? pasted.nodes[0]!.name : `${ids.length} nodes`;
      const links = pasted.links.length ? ` with ${pasted.links.length} ${pasted.links.length === 1 ? "link" : "links"}` : "";
      toast(`Pasted ${what}${links}. Press Ctrl+Z to undo.`);
    },
    menu(at) {
      return {
        copy(ids) {
          const text = clipboard.copy(ids);
          if (!text) return;
          void navigator.clipboard
            ?.writeText(text)
            .then(() => toast(ids.length === 1 ? "Copied. Ctrl+V pastes it where the pointer is." : `Copied ${ids.length} nodes. Ctrl+V pastes them where the pointer is.`))
            .catch(() => toast("The browser refused to copy: use Ctrl+C.", "error"));
        },
        pasteHere() {
          if (!navigator.clipboard?.readText) return void toast("The browser does not let the page read the clipboard: use Ctrl+V.", "error");
          void navigator.clipboard
            .readText()
            .then((text) => clipboard.paste(text, at))
            .catch(() => toast("The browser refused to read the clipboard: use Ctrl+V.", "error"));
        },
      };
    },
  };
  return clipboard;
}
